import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "plazafy_cookie_consent_v1";
// Re-pedir consentimiento cada 12 meses (recomendación AEPD)
const EXPIRATION_DAYS = 365;

const DEFAULT_PREFERENCES = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt) return null;
    const ageDays = (Date.now() - parsed.savedAt) / (1000 * 60 * 60 * 24);
    if (ageDays > EXPIRATION_DAYS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState(() => readStored());
  const [showBanner, setShowBanner] = useState(() => !readStored());

  useEffect(() => {
    // Notificar a otros listeners (ej. metaPixel) cuando cambian las preferencias
    window.dispatchEvent(new CustomEvent("plazafy:consentChanged", { detail: consent }));
  }, [consent]);

  const save = useCallback((prefs) => {
    const next = {
      ...DEFAULT_PREFERENCES,
      ...prefs,
      necessary: true,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setConsent(next);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    save({ analytics: true, marketing: true });
  }, [save]);

  const rejectAll = useCallback(() => {
    save({ analytics: false, marketing: false });
  }, [save]);

  const openSettings = useCallback(() => setShowBanner(true), []);

  return {
    consent: consent || DEFAULT_PREFERENCES,
    hasConsent: !!consent,
    showBanner,
    acceptAll,
    rejectAll,
    save,
    openSettings,
    setShowBanner,
  };
}

// Helpers para uso fuera de React (ej. metaPixel.js)
export function getStoredConsent() {
  return readStored() || DEFAULT_PREFERENCES;
}

export function hasAnalyticsConsent() {
  const c = readStored();
  return !!c?.analytics;
}

export function hasMarketingConsent() {
  const c = readStored();
  return !!c?.marketing;
}
